import { useMemo, useCallback } from 'react'
import { PlatformUserDomainRecord } from '../../../../models/domain/platform-user-domain-record'
import { UserPreferencesApi } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { togglePinInList } from '../utils/pinned-logic'
import { filterValidIds } from '../utils/recents-logic'

/**
 * usePinned — reads and writes the pinned (favorited) domains list
 * via the preferences API at the unscoped layer.
 *
 * @param userPreferences - UserPreferencesApi instance (unscoped)
 * @param domainMap - Map of domainId → PlatformUserDomainRecord for validation
 * @param maxPinned - Maximum number of pinned domains to keep
 * @returns favorited array and togglePin function
 */
export function usePinned(
    userPreferences: UserPreferencesApi,
    domainMap: Map<number, PlatformUserDomainRecord>,
    maxPinned: number,
): {
    favorited: number[]
    togglePin: (domainId: number) => void
} {
    const rawPinned: number[] = userPreferences.get(['domain_selector_pinned'], [])

    const favorited = useMemo(() => {
        if (userPreferences.isLoading) {
            return []
        }

        return filterValidIds(rawPinned, domainMap)
    }, [userPreferences.isLoading, rawPinned, domainMap])

    const togglePin = useCallback(
        (domainId: number): void => {
            const result = togglePinInList(rawPinned, domainId, maxPinned)
            if (result !== null) {
                userPreferences.set(['domain_selector_pinned'], result)
            }
        },
        [rawPinned, maxPinned, userPreferences],
    )

    return {
        favorited,
        togglePin,
    }
}
